import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  notifySuccess,
  notifyInProgress,
  notifyError,
  notifyThrottled,
} from '../../../../../../ReactToastify/containers/ContentEditToast';
import {
  selectIsPolling,
  selectVideoEditorModalIsOpen,
  selectStockItemEdited,
  stopPolling,
  TaskStatus,
  videoFailed,
  videoSucceeded,
  openVideoEditorModal,
} from '../../../../../ContentEdit/ContentEditSlice';
import { BroadcastMessage } from './useCrossTabSync';

const POLLING_INTERVAL = 30000;
const POLLING_LIMIT = 20;
const API_ENDPOINT = '/api/video/generate-edit/status';

export const useVideoStatusPolling = (
  sendMessage?: (message: BroadcastMessage) => void,
  interval = POLLING_INTERVAL,
  limit = POLLING_LIMIT
) => {
  const intervalRef = useRef(null);
  const attemptCountRef = useRef(0);
  const isPolling = useSelector(selectIsPolling);
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const stockItemEdited = useSelector(selectStockItemEdited);
  const dispatch = useDispatch();

  const onSuccess = ({ outputUrl, stockItemEdited, jobId }) => {
    if (!isOpen) {
      notifySuccess(openEditorModal);
    }
    dispatch(videoSucceeded({ outputUrl, stockItemEdited, jobId }));

    // Broadcast success to other tabs
    sendMessage?.({
      type: 'JOB_SUCCEEDED',
      payload: {
        outputUrl,
        jobId,
        stockItemEdited,
      },
    });
  };

  const onError = ({ error, jobId }: { error: Error; jobId?: number }) => {
    notifyError();
    dispatch(videoFailed({ error: error.message, jobId }));

    // Broadcast failure to other tabs
    sendMessage?.({
      type: 'JOB_FAILED',
      payload: {
        error: error.message,
        jobId,
      },
    });
  };

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptCountRef.current = 0;
  }, []);

  const openEditorModal = useCallback(() => {
    if (stockItemEdited) {
      dispatch(openVideoEditorModal(stockItemEdited));
    }
  }, [stockItemEdited, dispatch]);

  const fetchVideoStatus = useCallback(async () => {
    attemptCountRef.current += 1;

    if (attemptCountRef.current > limit) {
      clearPollingInterval();
      dispatch(stopPolling());
      return;
    }

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const resultJson = await response.json();
      const data = resultJson.data;
      const taskStatus = data?.runway_task_status;

      switch (taskStatus) {
        case TaskStatus.SUCCEEDED: {
          const outputUrl = data?.output_url;
          const jobId = data?.job_id;
          clearPollingInterval();
          onSuccess({ outputUrl, stockItemEdited, jobId });
          break;
        }

        case TaskStatus.RUNNING:
        case TaskStatus.PENDING:
          if (!isOpen) {
            notifyInProgress(openEditorModal);
          }
          break;

        case TaskStatus.THROTTLED:
          if (!isOpen) {
            notifyThrottled(openEditorModal);
          }
          break;

        default: {
          console.error(
            'Video editing task has stopped with status:',
            taskStatus
          );
          const jobId = data?.job_id;
          clearPollingInterval();
          onError({
            error: new Error(`Task failed with status: ${taskStatus}`),
            jobId,
          });
          break;
        }
      }
    } catch (error) {
      console.error('Error getting video edit status:', error);
      clearPollingInterval();
      onError({ error, jobId: undefined });
    }
  }, [onSuccess, onError, clearPollingInterval, limit, dispatch]);

  useEffect(() => {
    if (isPolling) {
      if (!isOpen) {
        notifyInProgress(openEditorModal);
      }
      // Reset attempt counter when starting new polling session
      attemptCountRef.current = 0;
      intervalRef.current = setInterval(fetchVideoStatus, interval);

      return clearPollingInterval;
    } else {
      // Stop polling if isPolling becomes false
      clearPollingInterval();
    }
  }, [isPolling, fetchVideoStatus, interval, clearPollingInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return clearPollingInterval;
  }, [clearPollingInterval]);

  return {
    // Could be used to manually stop polling if needed
    clearPolling: clearPollingInterval,
  };
};
